/**
 * gameAudio/_context.ts — shared AudioContext, bus gain nodes, master on/off,
 * audio-unlock orchestration, and primitive synth + sample helpers used by all
 * other gameAudio/* bus modules. PR-16 split of lib/gameAudio.ts.
 *
 * Why this lives in one file: every bus (cue, petVoice, ambience, music,
 * feedbackVoice) must share the SAME AudioContext + the SAME master/bus gain
 * graph so that "audio off" silences everything and bus levels stay tuned
 * together. Splitting these into separate files would multiply the audio
 * graph and break that invariant.
 *
 * Circular-dep avoidance: setAudioEnabled() needs to stop ambience + music
 * when the kid mutes, and unlockAudio() needs to start them after the first
 * user gesture. Importing those bus modules from here would create a cycle.
 * Instead we expose a tiny lifecycle bus — registerOnAudioOff() and
 * registerOnAudioUnlock() — that each bus module subscribes to at import
 * time (the barrel imports every bus, so registrations always happen before
 * setAudioEnabled/unlockAudio fire).
 *
 * 调用关系：
 *   - All sibling files under gameAudio/* import { ensureAudio, getSfxGain,
 *     getAmbienceGain, getMusicGain, getMaster, tone, arpeggio, noiseTick,
 *     loadSample, sampleBufferCache } from here.
 *   - Re-exported by lib/gameAudio.ts (the barrel) so legacy consumers like
 *     AppShell, HomePage, QuizPage can keep `import { unlockAudio,
 *     getAudioEnabled, setAudioEnabled } from '../lib/gameAudio'`.
 *
 * 关键约束：
 *   - getXxxGain() returns a *fresh* getter — never cache the returned node
 *     across calls; stopBackgroundMusic() in music.ts rebuilds musicGain to
 *     guarantee no orphan source can still be audible after a stop.
 *   - Bus modules MUST NOT import each other to read these singletons; pull
 *     them from this module so the graph stays single-rooted.
 *   - MASTER/SFX/AMBIENCE/MUSIC gain levels are tuned together — bumping
 *     one without re-balancing the others will drown out the others.
 */

// localStorage keys for user-toggleable audio preferences. Naming carries
// the app prefix so two iframes / hosted PWAs can't clash with each other.
export const STORAGE_KEY = 'xiaopeiban_audio_enabled';

// Per-bus gain stages (0..1). Tuned together so SFX cut through ambience,
// and ambience sits above music. Don't bump one without rebalancing.
export const MASTER_GAIN = 0.96;
export const SFX_GAIN = 0.92;
export const AMBIENCE_GAIN = 0.22;
export const MUSIC_GAIN = 0.18; // light piano sits below ambience + SFX

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let sfxGain: GainNode | null = null;
let ambienceGain: GainNode | null = null;
let musicGain: GainNode | null = null;

/**
 * Lazy-create the AudioContext + bus graph after the first user gesture
 * (browsers suspend brand-new contexts otherwise). Returns null when called
 * during SSR or on a browser without Web Audio.
 */
export function ensureAudio(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (ctx) return ctx;
  const AudioCtor = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioCtor) return null;
  ctx = new AudioCtor();
  master = ctx.createGain();
  sfxGain = ctx.createGain();
  ambienceGain = ctx.createGain();
  musicGain = ctx.createGain();
  master.gain.value = MASTER_GAIN;
  sfxGain.gain.value = SFX_GAIN;
  ambienceGain.gain.value = AMBIENCE_GAIN;
  musicGain.gain.value = MUSIC_GAIN;
  sfxGain.connect(master);
  ambienceGain.connect(master);
  musicGain.connect(master);
  master.connect(ctx.destination);
  return ctx;
}

/** Raw current AudioContext if already created. Does NOT lazy-init — use this
 *  when you only need to operate on an already-running graph (e.g. cancelling
 *  scheduled gain values during a fade-out). */
export function getCtx(): AudioContext | null { return ctx; }

export function getMaster(): GainNode | null { return master; }
export function getSfxGain(): GainNode | null { return sfxGain; }
export function getAmbienceGain(): GainNode | null { return ambienceGain; }
export function getMusicGain(): GainNode | null { return musicGain; }

/** music.ts uses this when stopping background music: it tears down the
 *  current musicGain node so no in-flight source can ever ring through, then
 *  rebuilds a fresh one connected to master. Other bus modules should not
 *  touch this. */
export function replaceMusicGain(): GainNode | null {
  if (!ctx || !master) return musicGain;
  const old = musicGain;
  if (old) {
    try { old.disconnect(); } catch { /* already disconnected */ }
  }
  const next = ctx.createGain();
  next.gain.value = MUSIC_GAIN;
  next.connect(master);
  musicGain = next;
  return musicGain;
}

// ─── Audio on/off + unlock lifecycle ────────────────────────────────────────

export function getAudioEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  return window.localStorage.getItem(STORAGE_KEY) !== 'off';
}

// Bus modules subscribe here at import time so _context.ts can fan out
// "audio just went off" / "audio just unlocked" without importing them
// directly (which would create cycles).
type Listener = () => void;
const audioOffListeners = new Set<Listener>();
const audioUnlockListeners = new Set<Listener>();

export function registerOnAudioOff(fn: Listener): void { audioOffListeners.add(fn); }
export function registerOnAudioUnlock(fn: Listener): void { audioUnlockListeners.add(fn); }

export function setAudioEnabled(enabled: boolean) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, enabled ? 'on' : 'off');
  // Broadcast so any other component in this tab (e.g. AppShell music picker)
  // can update without polling. Cross-tab sync still works via the native
  // 'storage' event.
  window.dispatchEvent(new CustomEvent('xiaopeiban:audio-toggle', { detail: { enabled } }));
  if (!enabled) {
    for (const fn of audioOffListeners) {
      try { fn(); } catch { /* listener errors must not block others */ }
    }
    return;
  }
  void unlockAudio();
}

export async function unlockAudio() {
  const audio = ensureAudio();
  if (!audio) return;
  if (audio.state === 'suspended') await audio.resume();
  if (getAudioEnabled()) {
    for (const fn of audioUnlockListeners) {
      try { fn(); } catch { /* listener errors must not block others */ }
    }
  }
}

// ─── Primitive synth helpers (used by cue.ts + petVoice.ts) ─────────────────
//
// Inline oscillator + envelope tones, plus a noise tick. Bundle-light because
// no samples are required — cue.ts uses these as a fallback when a recorded
// UI clip hasn't loaded yet, and petVoice.ts builds species-specific chirps
// from them.

export function tone(freq: number, dur: number, type: OscillatorType, gain = 0.03, delay = 0) {
  const audio = ensureAudio();
  const sfx = getSfxGain();
  if (!audio || !sfx) return;
  const osc = audio.createOscillator();
  const env = audio.createGain();
  const t = audio.currentTime + delay;
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t);
  env.gain.setValueAtTime(0.0001, t);
  env.gain.exponentialRampToValueAtTime(gain, t + 0.008);
  env.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  osc.connect(env);
  env.connect(sfx);
  osc.start(t);
  osc.stop(t + dur + 0.02);
}

export function arpeggio(freqs: number[], dur: number, gap: number, gain = 0.032) {
  freqs.forEach((freq, index) => tone(freq, dur, 'sine', gain, index * gap));
}

export function noiseTick(dur: number, gain = 0.016) {
  const audio = ensureAudio();
  const sfx = getSfxGain();
  if (!audio || !sfx) return;
  const buffer = audio.createBuffer(1, audio.sampleRate * dur, audio.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i += 1) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
  const src = audio.createBufferSource();
  const env = audio.createGain();
  const filter = audio.createBiquadFilter();
  filter.type = 'highpass';
  filter.frequency.value = 1800;
  env.gain.value = gain;
  src.buffer = buffer;
  src.connect(filter);
  filter.connect(env);
  env.connect(sfx);
  src.start();
}

// ─── Shared sample loader (used by cue.ts, petVoice.ts, feedbackVoice.ts) ───
//
// Three bus modules need to fetch+decode arbitrary mp3/ogg paths and cache
// the result. We centralise the cache here so two modules requesting the same
// path share one decode and one AudioBuffer in memory. The cache is a flat
// Map<path, AudioBuffer>; module-specific concerns (which path corresponds
// to which mood/cue/feedback line) stay in the calling module.

export const sampleBufferCache = new Map<string, AudioBuffer>();
const sampleLoading = new Map<string, Promise<AudioBuffer | null>>();
const sampleFailed = new Set<string>();

export async function loadSample(path: string): Promise<AudioBuffer | null> {
  if (sampleBufferCache.has(path)) return sampleBufferCache.get(path) || null;
  if (sampleFailed.has(path)) return null;
  const inflight = sampleLoading.get(path);
  if (inflight) return inflight;
  const audio = ensureAudio();
  if (!audio) return null;
  const promise = (async () => {
    try {
      const res = await fetch(path);
      if (!res.ok) throw new Error(`status ${res.status}`);
      const arr = await res.arrayBuffer();
      const buf = await audio.decodeAudioData(arr.slice(0));
      sampleBufferCache.set(path, buf);
      return buf;
    } catch {
      sampleFailed.add(path);
      return null;
    } finally {
      sampleLoading.delete(path);
    }
  })();
  sampleLoading.set(path, promise);
  return promise;
}
